import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck,
  Mail,
  Phone,
  Clock,
  Send,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  LogOut,
  Filter,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { GoldBellMark } from '../components/NoFeeLogo';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = 'nofee_admin_key';

const PendingRestaurantsAdmin = () => {
  const [adminKey, setAdminKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [keyError, setKeyError] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ count: 0, restaurants: [] });
  const [filter, setFilter] = useState('all'); // 'all' | '48h' | 'cold'
  const [resendStatus, setResendStatus] = useState({}); // { restaurantId: 'sending' | 'done' | 'error' }

  // Restore key from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(KEY_STORAGE);
    if (stored) {
      setAdminKey(stored);
      setAuthed(true);
    }
  }, []);

  const fetchData = useCallback(async (key) => {
    setLoading(true);
    setKeyError('');
    try {
      const res = await axios.get(`${API}/admin/pending-restaurants`, {
        headers: { 'X-Admin-Key': key },
      });
      setData(res.data);
      setAuthed(true);
      localStorage.setItem(KEY_STORAGE, key);
    } catch (err) {
      if (err.response?.status === 403) {
        setKeyError('Invalid admin key.');
        localStorage.removeItem(KEY_STORAGE);
        setAuthed(false);
      } else {
        setKeyError(err.response?.data?.detail || 'Failed to load pending restaurants.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed && adminKey) fetchData(adminKey);
  }, [authed, adminKey, fetchData]);

  const handleResend = async (restaurantId) => {
    setResendStatus((s) => ({ ...s, [restaurantId]: 'sending' }));
    try {
      await axios.post(
        `${API}/admin/pending-restaurants/${restaurantId}/resend-reminder`,
        {},
        { headers: { 'X-Admin-Key': adminKey } }
      );
      setResendStatus((s) => ({ ...s, [restaurantId]: 'done' }));
      setTimeout(() => setResendStatus((s) => { const c = { ...s }; delete c[restaurantId]; return c; }), 3500);
    } catch {
      setResendStatus((s) => ({ ...s, [restaurantId]: 'error' }));
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem(KEY_STORAGE);
    setAdminKey('');
    setAuthed(false);
    setData({ count: 0, restaurants: [] });
  };

  // ──────────────── Auth gate ────────────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-5" data-testid="admin-key-gate">
        <SEO title="Admin · Pending Restaurants" description="NoFee admin." path="/admin/pending-restaurants" noindex />
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5 mb-8 justify-center">
            <GoldBellMark size={36} />
            <div className="text-[22px] font-black tracking-tight text-white">
              No<span className="bg-gradient-to-b from-amber-400 via-gold-500 to-amber-700 bg-clip-text text-transparent">Fee</span>
              <span className="ml-2 text-white/40 text-sm font-medium tracking-wide">Admin</span>
            </div>
          </Link>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-7">
            <ShieldCheck className="w-7 h-7 text-gold-400 mb-4" />
            <h1 className="text-xl font-black text-white">Restricted area</h1>
            <p className="mt-1.5 text-[13px] text-white/55">Enter your admin key to continue.</p>
            <form
              onSubmit={(e) => { e.preventDefault(); if (adminKey.trim()) fetchData(adminKey.trim()); }}
              className="mt-6 space-y-3"
            >
              <input
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Admin key"
                autoFocus
                data-testid="admin-key-input"
                className="w-full px-4 h-12 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder:text-white/30 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition-all text-sm"
              />
              {keyError && (
                <div className="flex items-start gap-2 text-[12.5px] text-red-300">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{keyError}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-key-submit"
                className="w-full h-12 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-sm transition-all disabled:opacity-60"
              >
                {loading ? 'Verifying…' : 'Continue'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────── Main panel ────────────────
  const filtered = data.restaurants.filter((r) => {
    if (filter === 'all') return true;
    const h = r.hours_since_signup || 0;
    if (filter === '48h') return h >= 48;
    if (filter === 'cold') return h >= 168; // 1 week+
    return true;
  });

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-pending-restaurants">
      <SEO title="Admin · Pending Restaurants" description="NoFee admin." path="/admin/pending-restaurants" noindex />

      <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-white/[0.06] px-5 sm:px-8 py-4">
        <div className="max-w-[1300px] mx-auto flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <GoldBellMark size={32} />
            <div className="text-[18px] font-black tracking-tight">
              No<span className="bg-gradient-to-b from-amber-400 via-gold-500 to-amber-700 bg-clip-text text-transparent">Fee</span>
              <span className="ml-2 text-white/40 text-xs font-medium tracking-wide">Admin</span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fetchData(adminKey)}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 text-[12.5px] font-semibold transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl text-white/55 hover:text-white text-[12.5px] font-semibold transition-all"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold">Recovery funnel</p>
            <h1 className="mt-1.5 text-3xl sm:text-4xl font-black tracking-tight">
              Pending restaurants <span className="text-white/40 tabular-nums">· {filtered.length}</span>
            </h1>
            <p className="mt-2 text-[13.5px] text-white/55 max-w-xl">
              Restaurants that signed up but haven't completed Stripe Checkout. Resend a reminder email or contact them directly.
            </p>
          </div>
          <div className="inline-flex items-center gap-1.5 p-1 rounded-xl bg-white/[0.04] border border-white/10" data-testid="admin-filter-group">
            {[
              { id: 'all', label: 'All' },
              { id: '48h', label: '48h+' },
              { id: 'cold', label: 'Cold (1w+)' },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`admin-filter-${f.id}`}
                className={`px-3 h-9 rounded-lg text-[12.5px] font-semibold transition-all ${
                  filter === f.id
                    ? 'bg-gold-500/15 text-gold-200 border border-gold-500/30'
                    : 'text-white/55 hover:text-white border border-transparent'
                }`}
              >
                <Filter className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading && data.restaurants.length === 0 ? (
          <div className="py-16 text-center text-white/40 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-white/55 text-sm" data-testid="admin-empty">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            No pending restaurants match this filter.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="admin-list">
            {filtered.map((r, idx) => (
              <div
                key={r.id}
                data-testid={`admin-row-${r.id}`}
                className={`flex flex-col lg:flex-row lg:items-center gap-4 px-5 lg:px-6 py-5 ${
                  idx !== filtered.length - 1 ? 'border-b border-white/[0.06]' : ''
                } hover:bg-white/[0.02] transition-colors`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2.5 flex-wrap">
                    <h3 className="text-[15.5px] font-bold text-white truncate">{r.name}</h3>
                    <span className="text-[11px] uppercase tracking-[0.16em] text-gold-300/80 font-bold">
                      {r.plan || 'ordering'}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-[12.5px] text-white/55">
                    <span className="inline-flex items-center gap-1.5">
                      <Mail className="w-3 h-3" /> {r.owner_email || '—'}
                    </span>
                    {r.phone && (
                      <span className="inline-flex items-center gap-1.5">
                        <Phone className="w-3 h-3" /> {r.phone}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 tabular-nums">
                      <Clock className="w-3 h-3" />
                      {r.hours_since_signup != null ? `${r.hours_since_signup}h since signup` : '—'}
                    </span>
                  </div>
                  {r.reminders && r.reminders.length > 0 && (
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      {r.reminders.map((rm, i) => (
                        <span
                          key={i}
                          className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold border ${
                            rm.cancelled
                              ? 'border-white/10 text-white/40 line-through'
                              : rm.sent
                              ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/[0.05]'
                              : 'border-amber-500/30 text-amber-300 bg-amber-500/[0.05]'
                          }`}
                        >
                          {rm.kind} · {rm.cancelled ? 'cancelled' : rm.sent ? 'sent' : 'pending'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 lg:flex-shrink-0">
                  {resendStatus[r.id] === 'done' ? (
                    <span className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl text-[12.5px] font-bold bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Queued
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleResend(r.id)}
                      disabled={resendStatus[r.id] === 'sending'}
                      data-testid={`admin-resend-${r.id}`}
                      className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black text-[12.5px] font-extrabold transition-all disabled:opacity-60"
                    >
                      <Send className="w-3.5 h-3.5" />
                      {resendStatus[r.id] === 'sending' ? 'Queueing…' : 'Resend reminder'}
                    </button>
                  )}
                  {r.owner_email && (
                    <a
                      href={`mailto:${r.owner_email}?subject=${encodeURIComponent('Following up on your NoFee setup')}`}
                      className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 hover:text-gold-300 transition-all"
                      title="Open email client"
                    >
                      <Mail className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PendingRestaurantsAdmin;
